import {AbstractQueryBuilder, ExtractSortMap, IQueryMarkers, QueryCycleSortMarker} from '@global-data';
import dayjs from 'dayjs';

export interface ICycleQueryMarkers extends IQueryMarkers {
	lists: string[];
	sortDesc: boolean;
	fromDate: Date | null;
	toDate: Date | null;
	fromCreateDate: Date | null;
	toCreateDate: Date | null;
}

// הגדרת טיפוס מפתחות המיון לסבבים
export const CycleSortType = {
	[QueryCycleSortMarker.LastUpdate]: QueryCycleSortMarker.LastUpdate,
	[QueryCycleSortMarker.CreateDate]: QueryCycleSortMarker.CreateDate,
	[QueryCycleSortMarker.Creator]: QueryCycleSortMarker.Creator,
} as const;
export type CycleSortType = keyof typeof CycleSortType;

export type CycleQueryDateRangeType = 'LastUpdate' | 'CreateDate';

export class CycleQueryBuilder extends AbstractQueryBuilder<ICycleQueryMarkers, ExtractSortMap<typeof CycleSortType>> {
	private static readonly defaults: ICycleQueryMarkers = {
		searchText: '',
		sortBy: QueryCycleSortMarker.LastUpdate,
		sortDesc: false,
		lists: [],
		fromDate: null,
		toDate: null,
		fromCreateDate: null,
		toCreateDate: null,
	};

	// יישום של sortByMap הנדרש על ידי AbstractQueryBuilder
	protected readonly sortByMap = this.createSortByMap(CycleSortType);

	public static create(): CycleQueryBuilder {
		return new CycleQueryBuilder();
	}

	private constructor() {
		super(CycleQueryBuilder.defaults);
	}

	public setLists(lists: string[]): this {
		return this.updateMarkers({lists});
	}

	public setDateRange(from: Date, to: Date, dateRangeType: CycleQueryDateRangeType): this {
		if (dateRangeType === 'LastUpdate') {
			return this.updateMarkers({fromDate: from, toDate: to});
		} else if (dateRangeType === 'CreateDate') {
			return this.updateMarkers({fromCreateDate: from, toCreateDate: to});
		}
		return this;
	}

	public setCreateDateRange(from: Date, to: Date): this {
		return this.updateMarkers({fromCreateDate: from, toCreateDate: to});
	}

	protected override buildFixedParts(): string[] {
		return [];
	}

	public override formatMarkers(m: Partial<ICycleQueryMarkers>): string[] {
		const markers: string[] = [];

		// Handle search text (this seems to be a free text search - without markers)
		if (m.searchText) {
			markers.push(m.searchText);
		}

		// Handle lists (which seems to represent listType in the query function)
		if (m.lists && m.lists.length > 0) {
			markers.push(`type:[${m.lists.join(',')}]`);
		}

		// Handle sorting
		if (m.sortBy) {
			markers.push(`sortBy:${m.sortBy}`);
		}

		if (m.sortDesc !== undefined) {
			markers.push(`sortDesc:${m.sortDesc}`);
		}

		// Handle date ranges
		if (m.fromDate && m.toDate) {
			markers.push(`fromDate:${dayjs(m.fromDate).format()}`);
			markers.push(`toDate:${dayjs(m.toDate).format()}`);
		}

		if (m.fromCreateDate && m.toCreateDate) {
			markers.push(`fromCreateDate:${dayjs(m.fromCreateDate).format()}`);
			markers.push(`toCreateDate:${dayjs(m.toCreateDate).format()}`);
		}

		return markers;
	}
}
